import React from 'react';
import { FullBlock, autoFontColor, loadFont, BlockEditContext, StableInlineE } from '../content-editor/DashboardParts';
import SectionHoverOverlay from '../sqs/SectionHoverOverlay';
import HeroBlock from '../blocks/HeroBlock';
import {
  ProductsBlock, AboutBlock, GalleryBlock, TestimonialBlock, SignupBlock,
  CustomTextBlock, SingleImageBlock, VideoBlock, BookingBlock, DonationsBlock,
  FormBlock, BannerBlock, EmbedBlock, SpacerBlock, MarqueeBlock, FluidCanvasBlock,
} from '../blocks/CanvasBlocks';
import { renderPremiumSection } from '../premium/PremiumSectionRenderer';
import { DEFAULT_SWATCHES } from './siteTokens';

/* The ONE block renderer for the website surface. Edit mode and Live Preview
 * both call this, so the section DOM is byte-for-byte the same. Edit chrome
 * (hover toolbars) is only added when `readOnly` is false, and it is always
 * absolutely positioned so it never takes layout space. */

// Module-level stable BgLayer (reads visuals from BlockEditContext).
const StableBgLayer = ({ children, defaultBg = 'bg-white', hasPadding = true, center = false }) => {
  const ctx = React.useContext(BlockEditContext);
  const { block, c, s, bgColor, bgImage, fontColor, blockStyle, layoutStyle, isNarrow } = ctx || {};
  const hasBgImg = bgImage || (block?.type === 'hero' && c?.image_url);
  const style = {};
  if (bgColor && !hasBgImg) style.backgroundColor = bgColor;
  const autoColor = hasBgImg ? '#ffffff' : autoFontColor(bgColor, false);
  const textColor = fontColor || autoColor;
  const align = (s && s.alignment) || (center ? 'center' : '');
  const maxW = isNarrow ? 'max-w-2xl mx-auto' : '';
  return (
    <div className={`relative overflow-hidden ${hasPadding ? 'py-16 px-8' : ''} ${!bgColor && !bgImage ? defaultBg : ''}`} style={{ ...style, color: textColor, ...(blockStyle || {}), ...(layoutStyle || {}) }}>
      {bgImage && <img src={bgImage.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${bgImage}` : bgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      {bgImage && <div className="absolute inset-0 bg-black/40" />}
      <div className={`relative z-10 ${maxW} ${align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : align === 'left' ? 'text-left' : ''}`}>{children}</div>
    </div>
  );
};

const noop = () => {};

export function renderSiteBlock(block, {
  allProducts = [], tokens = {}, readOnly = false,
  onEdit = noop, onMediaPick = noop, onDelete, onMoveUp, onMoveDown,
  isFirst, isLast, onDuplicate, onAddBlock, onViewLayouts, onEditSection,
  onFavorite, onSetHeight, onSetAttached, isSelected,
} = {}) {
  const c = block.content || {};
  const s = c._style || {};
  const fontFamily = s.fontFamily || 'inherit';
  const fontColor = s.fontColor || '';
  const bgColor = s.bgColor || '';
  const bgImage = s.bgImage || '';
  const imgSize = s.imageSize || 'large';
  const imgSizeCls = { small: 'max-w-sm max-h-48', medium: 'max-w-lg max-h-72', large: 'max-w-3xl max-h-96', full: 'max-w-full' }[imgSize] || 'max-w-3xl';
  if (fontFamily !== 'inherit') loadFont(fontFamily);

  const blockStyle = {};
  if (fontFamily !== 'inherit') blockStyle.fontFamily = fontFamily;
  if (fontColor) blockStyle.color = fontColor;
  const layoutId = s.layout || 'centered';
  const layoutStyle = {};
  if (s.alignment) layoutStyle.textAlign = s.alignment;
  const isNarrow = s.maxWidth === 'narrow';
  const isImageTop = s.imagePosition === 'top';
  const isImageBottom = s.imagePosition === 'bottom';
  const isOverlay = layoutId === 'overlay' || s.imagePosition === 'background';

  const BgLayer = StableBgLayer;
  const fieldStyles = c._field_styles || {};
  const E = StableInlineE;
  const editCtx = { blockId: block.id, onEdit, fieldStyles, block, c, s, bgColor, bgImage, fontColor, blockStyle, layoutStyle, isNarrow };

  // Invoked as a plain function (`{Toolbar()}`) so SectionHoverOverlay keeps a
  // stable component type across renders (prevents hover strobe).
  const Toolbar = readOnly ? () => null : () => (
    <SectionHoverOverlay
      block={block}
      onEdit={() => onEditSection ? onEditSection(block.id) : onEdit(block.id, '_open_panel', true)}
      onViewLayouts={() => onViewLayouts && onViewLayouts(block.id)}
      onDuplicate={() => onDuplicate && onDuplicate(block.id)}
      onFavorite={() => onFavorite && onFavorite(block.id)}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      onDelete={onDelete}
      onAddBlock={() => onAddBlock && onAddBlock(block.id)}
      onSetHeight={onSetHeight}
      onSetAttached={onSetAttached}
      isFirst={isFirst}
      isLast={isLast}
      isFavorited={!!(c._style?.favorited)}
      isSelected={!!isSelected}
    />
  );

  const palette = tokens.palette || DEFAULT_SWATCHES;
  const headingFont = tokens.headingFont || 'Syne';
  const radius = tokens.radius ?? 8;
  const premiumRender = renderPremiumSection(block, palette, headingFont, radius, onEdit, onMediaPick);
  const wrapInCtx = (inner) => (
    <BlockEditContext.Provider value={editCtx} key={block.id}>{inner}</BlockEditContext.Provider>
  );
  if (premiumRender) {
    return wrapInCtx(<FullBlock block={block}>{Toolbar()}{premiumRender}</FullBlock>);
  }

  const p = { block, content: c, E, Toolbar, FullBlock };
  switch (block.type) {
    case 'hero': return wrapInCtx(
      <FullBlock block={block}>{Toolbar()}
        <HeroBlock block={block} content={c} fieldStyles={fieldStyles} onEdit={onEdit} onMediaPick={onMediaPick} E={E} />
      </FullBlock>
    );
    case 'products': return wrapInCtx(<ProductsBlock {...p} style={s} BgLayer={BgLayer} onEdit={onEdit} allProducts={allProducts} />);
    case 'about': return wrapInCtx(<AboutBlock {...p} style={s} BgLayer={BgLayer} onEdit={onEdit} onMediaPick={onMediaPick} imgSizeCls={imgSizeCls} isImageTop={isImageTop} isImageBottom={isImageBottom} isOverlay={isOverlay} />);
    case 'gallery': return wrapInCtx(<GalleryBlock {...p} style={s} BgLayer={BgLayer} onEdit={onEdit} onMediaPick={onMediaPick} />);
    case 'testimonial': return wrapInCtx(<TestimonialBlock {...p} style={s} BgLayer={BgLayer} />);
    case 'signup': return wrapInCtx(<SignupBlock {...p} style={s} BgLayer={BgLayer} />);
    case 'custom_text': return wrapInCtx(<CustomTextBlock {...p} style={s} BgLayer={BgLayer} />);
    case 'single_image': return wrapInCtx(<SingleImageBlock {...p} style={s} BgLayer={BgLayer} onEdit={onEdit} onMediaPick={onMediaPick} imgSizeCls={imgSizeCls} />);
    case 'video': return wrapInCtx(<VideoBlock {...p} onEdit={onEdit} />);
    case 'booking': return wrapInCtx(<BookingBlock {...p} style={s} BgLayer={BgLayer} />);
    case 'donations': return wrapInCtx(<DonationsBlock {...p} style={s} BgLayer={BgLayer} />);
    case 'form': return wrapInCtx(<FormBlock {...p} onEdit={onEdit} />);
    case 'banner': return wrapInCtx(<BannerBlock {...p} style={s} onEdit={onEdit} bgColor={bgColor} bgImage={bgImage} blockStyle={blockStyle} />);
    case 'embed': return wrapInCtx(<EmbedBlock {...p} onEdit={onEdit} />);
    case 'spacer': return wrapInCtx(<SpacerBlock block={block} content={c} Toolbar={Toolbar} FullBlock={FullBlock} />);
    case 'marquee': return wrapInCtx(<MarqueeBlock block={block} content={c} Toolbar={Toolbar} FullBlock={FullBlock} />);
    case 'fluid_canvas': return wrapInCtx(<FluidCanvasBlock block={block} content={c} Toolbar={Toolbar} FullBlock={FullBlock} onEdit={onEdit} />);
    default: return wrapInCtx(
      <FullBlock block={block}>{Toolbar()}
        <div className="py-8 px-8 bg-gray-100 text-center"><p className="text-gray-500">Unknown block: {block.type}</p></div>
      </FullBlock>
    );
  }
}

export default renderSiteBlock;
